import React, { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

export default function TicketScanner({ onClose }) {
  const readerId = 'reader';

  const scannerRef = useRef(null);
  const activeRef = useRef(true);
  const stoppingRef = useRef(false);

  // Safely stop and clean up the scanner
  const safeStop = async () => {
    if (!scannerRef.current || stoppingRef.current) return;
    stoppingRef.current = true;

    try {
      const state = scannerRef.current.getState();
      // 2 = SCANNING, 3 = PAUSED
      if (state === 2 || state === 3) {
        await scannerRef.current.stop();
      }
    } catch (err) {
      // ignore stop errors
    }

    try {
      await scannerRef.current.clear();
    } catch (err) {
      // ignore clear errors
    }

    scannerRef.current = null;
    stoppingRef.current = false;
  };

  useEffect(() => {
    activeRef.current = true;

    // Lock background scroll while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const startScanner = async () => {
      try {
        // Ensure reader element exists
        const el = document.getElementById(readerId);
        if (!el) return;

        scannerRef.current = new Html5Qrcode(readerId);

        await scannerRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            if (!activeRef.current) return;

            try {
              const res = await fetch('/api/verify-ticket', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ qrCode: decodedText }),
              });

              const data = await res.json();

              if (res.ok && data.success) {
                alert(`Ticket verified: ${data.ticket.buyerEmail}`);
              } else {
                alert('Ticket not found or already used');
              }
            } catch (err) {
              console.error(err);
              alert('Error verifying ticket');
            }

            activeRef.current = false;
            await safeStop();
            onClose();
          },
          () => {
            // ignore scan errors
          }
        );
      } catch (err) {
        console.error('Scanner failed to start', err);
        alert('Unable to access camera');
        onClose();
      }
    };

    // Delay start to ensure DOM is painted (CRITICAL)
    const timer = setTimeout(startScanner, 100);

    return () => {
      clearTimeout(timer);
      activeRef.current = false;
      document.body.style.overflow = originalOverflow;
      safeStop();
    };
  }, [onClose]);

  const closeScanner = async () => {
    activeRef.current = false;
    await safeStop();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center"
      onClick={closeScanner}
    >
      {/* Modal container */}
      <div
        className="bg-black p-4 rounded"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id={readerId}
          className="w-80 h-80 bg-white rounded"
        />

        <button
          onClick={closeScanner}
          className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded"
        >
          Close Scanner
        </button>
      </div>
    </div>
  );
}
